/**
 * Decision cards (13-10), built to design/mock-13 (D-13-02-1).
 *
 * `ActionCard` sends every `Kind = Decision` row here. `isRegulatoryDecision(row)` (fail-safe,
 * D-13-00-2 / D-13-05-2) picks the variant:
 *
 *   - **Ordinary** (`DecisionInset`): the recommendation first, with its reasoning and confidence,
 *     and one tap to Agree. The other options sit beside it as secondary buttons, and "Something
 *     else…" opens a free-text decision. With no recommendation the options are shown as equals;
 *     with no readable options the raw text is shown and the decision is typed. Every choice goes
 *     through `decide` → the outbox → the tick and a 6 s undo, as in 13-09.
 *   - **Regulatory** (`RegulatoryVerbs`): no suggestion and no one-tap. Oversight gets a charcoal
 *     "Decide" that opens the regulatory panel; anyone else reads "Waiting on Oversight". The line
 *     saying why it is treated as regulatory is always shown, so a wrong classification is visible.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { canDoVerb, isRegulatoryDecision } from '../../lib/actionAuthority';
import { decidedOptionValue, DECIDED_OPTION_MAX, optionFor, optionText, parseOptions } from '../../lib/options';
import type { ParsedOption } from '../../lib/options';
import { regulatoryBecauseText } from '../../lib/todayQueue';
import type { VerbInput } from '../../lib/actionVerbs';
import type { RunCopy, RunResult } from '../../hooks/useActionQueue';
import type { ActionRow, Actor } from '../../types/actionRegister';
import styles from './today.module.css';

const cx = (...names: Array<string | false | null | undefined>) => names.filter(Boolean).join(' ');

type Run = (input: VerbInput, copy: RunCopy) => RunResult;

// ---------------------------------------------------------------------------
// Ordinary
// ---------------------------------------------------------------------------

export interface DecisionInsetProps {
  row: ActionRow;
  actor: Actor;
  busy: boolean;
  /** `decide` on this row (the card adds the refusal handling). */
  decide: Run;
}

type Choice = { kind: 'option'; option: ParsedOption } | { kind: 'other' };

export function DecisionInset({ row, actor, busy, decide }: DecisionInsetProps) {
  const parsed = useMemo(() => parseOptions(row.options), [row.options]);
  const recommended = optionFor(parsed, parsed.recommended);
  const others = parsed.options.filter((o) => o.key !== parsed.recommended);
  const noOptions = parsed.options.length === 0;

  const [pick, setPick] = useState<Choice | null>(noOptions ? { kind: 'other' } : null);
  const [why, setWhy] = useState('');
  const [own, setOwn] = useState('');
  const field = useRef<HTMLInputElement | null>(null);
  const opened = useRef(false);

  useEffect(() => {
    // Focus the drawer's field when the person opened it (not on first render of a no-options card).
    if (pick && opened.current) field.current?.focus();
  }, [pick]);

  if (!canDoVerb('decide', row, actor).ok) {
    return (
      <div className={styles.rec}>
        <p className={styles.recText}>{row.options?.trim() || 'No options recorded on this row.'}</p>
        <p className={styles.hint}>This isn&rsquo;t yours to decide.</p>
      </div>
    );
  }

  const open = (p: Choice) => {
    opened.current = true;
    setWhy('');
    setPick(p);
  };
  const close = () => setPick(noOptions ? { kind: 'other' } : null);

  const agree = () => {
    if (!recommended) return;
    decide({ option: decidedOptionValue(recommended) }, { title: `Agreed: ${optionText(recommended.label)}`, description: row.title });
  };

  const confirm = () => {
    if (!pick) return;
    const reason = why.trim() || undefined;
    if (pick.kind === 'option') {
      decide(
        { option: decidedOptionValue(pick.option), reason },
        { title: `Decided: ${optionText(pick.option.label)}`, description: reason ? `“${reason}”` : row.title },
      );
    } else {
      const text = own.trim();
      if (!text) return;
      decide({ option: decidedOptionValue(text), reason }, { title: `Decided: ${text}`, description: row.title });
    }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirm();
    } else if (e.key === 'Escape' && !noOptions) {
      e.preventDefault();
      close();
    }
  };

  const id = `d${row.id}`;
  const altLabel = pick?.kind === 'option' ? optionText(pick.option.label) : null;

  return (
    <>
      <div className={styles.rec}>
        {recommended ? (
          <>
            <div className={styles.recLabel}>Suggested</div>
            <div className={styles.recChoice}>{optionText(recommended.label)}</div>
            {parsed.reasoning && <p className={styles.recWhy}>{sentence(parsed.reasoning)}</p>}
            {(parsed.confidence !== undefined || parsed.remark) && (
              <p className={styles.recMeta}>
                {parsed.confidence !== undefined && <span>The agent&rsquo;s confidence: {parsed.confidence}%</span>}
                {parsed.remark && <span>{sentence(parsed.remark)}</span>}
              </p>
            )}
            <div className={styles.recActs}>
              <button type="button" data-primary className={cx(styles.btn, styles.go)} onClick={agree} disabled={busy}>
                Agree
              </button>
              {others.length > 0 && <span className={styles.or}>or</span>}
              {others.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  className={cx(styles.btn, styles.quiet, styles.sm, styles.alt)}
                  onClick={() => open({ kind: 'option', option: o })}
                  disabled={busy}
                  aria-expanded={pick?.kind === 'option' && pick.option.key === o.key}
                >
                  {optionText(o.label)}
                </button>
              ))}
              <button
                type="button"
                className={cx(styles.btn, styles.quiet, styles.sm)}
                onClick={() => open({ kind: 'other' })}
                disabled={busy}
                aria-expanded={pick?.kind === 'other'}
              >
                Something else&hellip;
              </button>
            </div>
          </>
        ) : noOptions ? (
          <>
            <div className={styles.recLabel}>What&rsquo;s being asked</div>
            <p className={styles.recText}>{row.options?.trim() || row.description?.trim() || 'No options recorded on this row.'}</p>
          </>
        ) : (
          <>
            <div className={styles.recLabel}>The options</div>
            <p className={styles.hint} style={{ marginTop: 2 }}>
              No suggestion was given, so they&rsquo;re listed as written.
            </p>
            <div className={styles.equalOpts}>
              {parsed.options.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  className={cx(styles.btn, styles.line, styles.alt)}
                  onClick={() => open({ kind: 'option', option: o })}
                  disabled={busy}
                  aria-expanded={pick?.kind === 'option' && pick.option.key === o.key}
                  data-primary={o.key === parsed.options[0].key ? true : undefined}
                >
                  {optionText(o.label)}
                </button>
              ))}
              <button
                type="button"
                className={cx(styles.btn, styles.quiet, styles.sm)}
                onClick={() => open({ kind: 'other' })}
                disabled={busy}
                aria-expanded={pick?.kind === 'other'}
              >
                Something else&hellip;
              </button>
            </div>
          </>
        )}
      </div>

      {pick && (
        <div className={styles.drawer}>
          {pick.kind === 'option' ? (
            <label htmlFor={`${id}-why`}>
              Go with &ldquo;{altLabel}&rdquo;? Add a reason <span className={styles.opt}>(optional)</span>
            </label>
          ) : (
            <label htmlFor={`${id}-own`}>Your decision</label>
          )}
          {pick.kind === 'other' && (
            <div className={styles.field}>
              <input
                ref={field}
                id={`${id}-own`}
                className={styles.input}
                value={own}
                maxLength={DECIDED_OPTION_MAX}
                onChange={(e) => setOwn(e.target.value)}
                onKeyDown={onKey}
                placeholder="What you've decided, in one line"
                autoComplete="off"
              />
            </div>
          )}
          <div className={styles.field} style={pick.kind === 'other' ? { marginTop: 8 } : undefined}>
            <input
              ref={pick.kind === 'option' ? field : undefined}
              id={`${id}-why`}
              aria-label={pick.kind === 'other' ? 'Reason (optional)' : undefined}
              className={styles.input}
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              onKeyDown={onKey}
              placeholder={pick.kind === 'option' && recommended ? 'Why not the suggestion?' : 'Reason (optional)'}
              autoComplete="off"
            />
            <button
              type="button"
              className={cx(styles.btn, styles.line, styles.sm)}
              onClick={confirm}
              disabled={busy || (pick.kind === 'other' && !own.trim())}
            >
              Decide
            </button>
            {!noOptions && (
              <button type="button" className={cx(styles.btn, styles.quiet, styles.sm)} onClick={close}>
                Cancel
              </button>
            )}
          </div>
          {pick.kind === 'other' && <p className={styles.hint}>A reason is kept with the decision, in its evidence.</p>}
        </div>
      )}
    </>
  );
}

/** Capitalise the first letter and end with a full stop: the parsed text is a clause. */
function sentence(text: string): string {
  const t = text.trim();
  if (!t) return t;
  const first = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?)]$/.test(first) ? first : `${first}.`;
}

// ---------------------------------------------------------------------------
// Regulatory
// ---------------------------------------------------------------------------

export interface RegulatoryVerbsProps {
  row: ActionRow;
  actor: Actor;
  busy: boolean;
  /** Opens the regulatory panel: to decide (Oversight) or to read (anyone else). */
  onOpen: (row: ActionRow, readOnly: boolean) => void;
}

export function RegulatoryVerbs({ row, actor, busy, onOpen }: RegulatoryVerbsProps) {
  const because = regulatoryBecauseText(isRegulatoryDecision(row).because);
  // Oversight only (D-13-00-5); `canDoVerb` classifies the row again itself (D-13-05-2).
  const mayDecide = canDoVerb('decide', row, actor, { regulatory: true }).ok;
  return (
    <div className={styles.verbs}>
      {mayDecide ? (
        <>
          <button type="button" data-primary className={cx(styles.btn, styles.solemn)} onClick={() => onOpen(row, false)} disabled={busy}>
            Decide
          </button>
          <span className={styles.sub}>Opens the full context. No suggestion is shown for regulatory calls.</span>
        </>
      ) : (
        <>
          <span className={styles.waiting}>Waiting on Oversight</span>
          <button type="button" className={cx(styles.btn, styles.quiet, styles.sm)} onClick={() => onOpen(row, true)}>
            View context
          </button>
        </>
      )}
      <p className={styles.because}>{because}</p>
    </div>
  );
}
